import React, {
  useState, useEffect, useMemo, useRef, useCallback
} from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import styles from "./TransferErpData.module.css";

/* ════════════════════════════════════════════════════════════
   COLUMN DEFINITIONS
   type: 'text' | 'amount' | 'date' | 'flag'
   default: visible by default
   width: px width for table-layout:fixed
════════════════════════════════════════════════════════════ */
const COLUMNS = [
  { key:"ID",                    label:"ID",             type:"text",   width:60,  default:false },
  { key:"ENTITY_CODE",           label:"Entity",         type:"text",   width:54,  default:true  },
  { key:"VR_DATE",               label:"Vr. Date",       type:"date",   width:78,  default:true  },
  { key:"VOUCHER_NO",            label:"Voucher No",     type:"text",   width:100, default:true  },
  { key:"PARTY_CODE",            label:"Party Code",     type:"text",   width:72,  default:false },
  { key:"PARTY_NAME",            label:"Party Name",     type:"text",   width:160, default:true  },
  { key:"GSTIN",                 label:"GSTIN",          type:"text",   width:130, default:true  },
  { key:"PAN",                   label:"PAN",            type:"text",   width:90,  default:false },
  { key:"PARTY_BILL_NO",         label:"Bill No",        type:"text",   width:110, default:true  },
  { key:"PARTY_BILL_DATE",       label:"Bill Date",      type:"date",   width:78,  default:true  },
  { key:"ASSESSABLE_VALUE",      label:"Assessable",     type:"amount", width:90,  default:true  },
  { key:"IGST_AMOUNT",           label:"IGST",           type:"amount", width:78,  default:true  },
  { key:"CGST_AMOUNT",           label:"CGST",           type:"amount", width:78,  default:true  },
  { key:"SGST_AMOUNT",           label:"SGST",           type:"amount", width:78,  default:true  },
  { key:"CESS_AMOUNT",           label:"CESS",           type:"amount", width:72,  default:false },
  { key:"TOTAL_TAX_VALUE",       label:"Total Tax",      type:"amount", width:84,  default:true  },
  { key:"BILL_AMOUNT",           label:"Bill Amt",       type:"amount", width:84,  default:true  },
  { key:"MAIN_POST_VALUE",       label:"Post Value",     type:"amount", width:84,  default:false },
  { key:"PARTY_BILL_AMOUNT",     label:"Party Bill Amt", type:"amount", width:90,  default:false },
  { key:"BRANCH_GSTIN",          label:"Branch GSTIN",   type:"text",   width:130, default:false },
  { key:"IS_RCM",                label:"RCM",            type:"flag",   width:42,  default:true  },
  { key:"RECONCILED_FLAG",       label:"Recon",          type:"flag",   width:48,  default:true  },
  { key:"RECONCILED_ON",         label:"Recon On",       type:"date",   width:78,  default:false },
  { key:"RECONCILED_TYPE",       label:"Recon Type",     type:"text",   width:84,  default:false },
  { key:"RECONCILED_AGAINST",    label:"Recon Agnst",    type:"text",   width:78,  default:false },
  { key:"PROCESS_MONTH",         label:"Proc Month",     type:"date",   width:78,  default:false },
  { key:"ADDON_CODE",            label:"Addon",          type:"text",   width:54,  default:false },
  { key:"ADDON_NAME",            label:"Addon Name",     type:"text",   width:120, default:false },
  { key:"ACC_VRNO",              label:"Acc VrNo",       type:"text",   width:84,  default:false },
  { key:"ACC_TCODE",             label:"TCode",          type:"text",   width:48,  default:false },
  { key:"TNATURE",               label:"T.Nature",       type:"text",   width:120, default:false },
  { key:"TNATURE_CODE",          label:"TN Code",        type:"text",   width:66,  default:false },
  { key:"ITEM_GROUP",            label:"Item Grp",       type:"text",   width:60,  default:false },
  { key:"ITEM_CATG",             label:"Item Catg",      type:"text",   width:60,  default:false },
  { key:"ITEM_CATG_NAME",        label:"Catg Name",      type:"text",   width:130, default:false },
  { key:"ITEM_GROUP_NAME",       label:"Grp Name",       type:"text",   width:130, default:false },
  { key:"ITEM_NAME",             label:"Item Name",      type:"text",   width:130, default:false },
  { key:"ITEM_NATURE",           label:"Nature",         type:"text",   width:54,  default:false },
  { key:"GST_CODE",              label:"GST Code",       type:"text",   width:72,  default:false },
  { key:"GST_FROM_PAN",          label:"GST/PAN",        type:"text",   width:110, default:false },
  { key:"FROM_GSTIN_TYPE",       label:"GSTIN Type",     type:"text",   width:84,  default:false },
  { key:"PARTICULARS",           label:"Particulars",    type:"text",   width:140, default:false },
  { key:"POST_CODE",             label:"Post Code",      type:"text",   width:90,  default:false },
  { key:"LASTUPDATE",            label:"Last Update",    type:"date",   width:78,  default:false },
  { key:"APPROVEDDATE",          label:"Approved",       type:"date",   width:78,  default:false },
  { key:"CREATED_AT",            label:"Created",        type:"date",   width:78,  default:false },
  { key:"EXCLUDED_FLAG",         label:"Excl.",          type:"flag",   width:42,  default:true  },
  { key:"EXCLUDED_ON",           label:"Excl. On",       type:"date",   width:78,  default:false },
  { key:"EXCLUDED_BY",           label:"Excl. By",       type:"text",   width:66,  default:false },
];

const AMOUNT_KEYS = COLUMNS.filter(c => c.type === "amount").map(c => c.key);

/* ════════════════════════════════════════════════════════════
   HELPERS
════════════════════════════════════════════════════════════ */
const fmt = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const n = parseFloat(val);
  if (isNaN(n)) return String(val);
  if (n === 0) return "0";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const fmtDate = (val) => {
  if (!val) return "";
  try {
    const d = new Date(val);
    if (isNaN(d)) return String(val);
    return d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
  } catch { return String(val); }
};

const cellVal = (col, row) => {
  const v = row[col.key];
  if (col.type === "amount") return fmt(v);
  if (col.type === "date")   return fmtDate(v);
  return v === null || v === undefined ? "" : String(v);
};

const colKey2str = (row, key) => {
  const v = row[key];
  return v === null || v === undefined ? "(blank)" : String(v).trim() || "(blank)";
};

/* ════════════════════════════════════════════════════════════
   USER PREFERENCES — layered: localStorage (cache) + server (source of truth)
   Screen key identifies which screen these prefs belong to
════════════════════════════════════════════════════════════ */
const SCREEN_KEY   = "erp_detail_grid";
const LS_KEY       = (userId) => `erp_prefs_${SCREEN_KEY}_${userId || "default"}`;
const DEFAULT_ORDER = COLUMNS.map(c => c.key);
const DEFAULT_VIS   = Object.fromEntries(COLUMNS.map(c => [c.key, c.default]));
const DEFAULT_PREFS = { vis: DEFAULT_VIS, order: DEFAULT_ORDER, sortOrder: [] };

/* ── Merge saved prefs with current COLUMNS (handles new columns added to code) ── */
const mergePrefs = (saved = {}) => {
  const savedOrder = Array.isArray(saved.order) ? saved.order : DEFAULT_ORDER;
  const newKeys    = DEFAULT_ORDER.filter(k => !savedOrder.includes(k));
  return {
    vis:       { ...DEFAULT_VIS,   ...(saved.vis       || {}) },
    order:     [...savedOrder, ...newKeys],
    sortOrder: Array.isArray(saved.sortOrder) ? saved.sortOrder : [],
  };
};

/* ── Read from localStorage instantly (used on first render, no flicker) ── */
const readLocalPrefs = (userId) => {
  try {
    const raw = localStorage.getItem(LS_KEY(userId));
    if (raw) return mergePrefs(JSON.parse(raw));
  } catch {}
  return { ...DEFAULT_PREFS };
};

/* ── Write to localStorage ── */
const writeLocalPrefs = (userId, prefs) => {
  try { localStorage.setItem(LS_KEY(userId), JSON.stringify(prefs)); } catch {}
};

/* ════════════════════════════════════════════════════════════
   SUB-COMPONENTS
════════════════════════════════════════════════════════════ */

/* ── Collapsible Panel ── */
const Panel = ({ title, open, onToggle, children, badge }) => (
  <div className={styles.panel}>
    <div className={styles.panelHeader} onClick={onToggle}>
      <div className={styles.panelHeaderLeft}>
        <span className={styles.panelToggle}>{open ? "▼" : "▶"}</span>
        <span className={styles.panelTitle}>{title}</span>
        {badge && <span style={{ fontSize:9, background:"#1b6ca8", color:"#fff", borderRadius:8, padding:"1px 6px", marginLeft:4 }}>{badge}</span>}
      </div>
    </div>
    {open && <div className={styles.panelBody}>{children}</div>}
  </div>
);

/* ── Compact Pagination ── */
const PagerBar = ({ page, totalPages, total, pageSize, onPage, onPageSize }) => {
  if (totalPages === 0) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    const diff = Math.abs(i - page);
    if (i === 1 || i === totalPages || diff <= 1) pages.push(i);
    else if (diff === 2) pages.push("...");
  }
  const deduped = pages.filter((v, i, a) => v !== "..." || a[i-1] !== "...");

  return (
    <div className={styles.pagination}>
      <button className={styles.pageBtn} onClick={() => onPage(1)} disabled={page===1}>«</button>
      <button className={styles.pageBtn} onClick={() => onPage(page-1)} disabled={page===1}>‹</button>
      {deduped.map((n, i) =>
        n === "..." ? (
          <span key={`e${i}`} style={{ fontSize:10, color:"#888", padding:"0 2px" }}>…</span>
        ) : (
          <button
            key={n}
            className={`${styles.pageBtn} ${n===page ? styles.pageBtnActive : ""}`}
            onClick={() => onPage(n)}
          >{n}</button>
        )
      )}
      <button className={styles.pageBtn} onClick={() => onPage(page+1)} disabled={page===totalPages}>›</button>
      <button className={styles.pageBtn} onClick={() => onPage(totalPages)} disabled={page===totalPages}>»</button>
      <span className={styles.pageInfo}>
        Page {page}/{totalPages} &nbsp;|&nbsp; {total.toLocaleString("en-IN")} records &nbsp;|&nbsp;
        <select
          className={styles.select}
          style={{ height:20, fontSize:10, minWidth:50, marginLeft:4 }}
          value={pageSize}
          onChange={e => onPageSize(parseInt(e.target.value))}
        >
          {[25,50,100,200,500].map(s => <option key={s} value={s}>{s}/pg</option>)}
        </select>
      </span>
    </div>
  );
};

/* ── Action Dropdown ── */
const ActionDropdown = ({ selectedCount, totalCount, onAction }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const actions = [
    { id:"show_all",          icon:"☰",  label:"Show All Records",                    needsSel:false, isFilter:true  },
    { id:"show_applicable",   icon:"✅", label:"Show GST Applicable Only",             needsSel:false, isFilter:true  },
    { id:"show_excluded",     icon:"❌", label:"Show GST Not Applicable Only",         needsSel:false, isFilter:true  },
    { sep: true },
    { id:"exclude_selected",  icon:"✕",  label:"Exclude Selected (GST Not Applicable)",needsSel:true  },
    { id:"include_selected",  icon:"✓",  label:"Include Selected (Restore Applicable)",needsSel:true  },
    { sep: true },
    { id:"mark_rcm",          icon:"📌", label:"Mark Selected as RCM",                needsSel:true  },
    { id:"unmark_rcm",        icon:"📌", label:"Unmark Selected RCM",                 needsSel:true  },
    { sep: true },
    { id:"export_csv",        icon:"📥", label:"Export Filtered Records to CSV",       needsSel:false },
    { id:"export_sel_csv",    icon:"📥", label:"Export Selected Records to CSV",       needsSel:true  },
  ];

  return (
    <div className={styles.actionDropWrap} ref={ref}>
      <button className={`${styles.btn} ${styles.btnDefault}`} onClick={() => setOpen(o => !o)}>
        ⚡ Actions {selectedCount > 0 && `(${selectedCount})`} ▾
      </button>
      {open && (
        <div className={styles.actionMenu}>
          {actions.map((a, i) => a.sep ? (
            <div key={`sep${i}`} className={styles.actionSep} />
          ) : (
            <div
              key={a.id}
              className={`${styles.actionItem} ${a.needsSel && selectedCount === 0 ? styles.actionItemDisabled : ""}`}
              onClick={() => {
                if (a.needsSel && selectedCount === 0) return;
                onAction(a.id);
                setOpen(false);
              }}
            >
              <span>{a.icon}</span>
              <span>{a.label}</span>
              {a.needsSel && selectedCount > 0 && (
                <span style={{ marginLeft:"auto", fontSize:9, color:"#888" }}>{selectedCount} rows</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const TransferErpData = () => {
  const user     = useSelector(selectUser);
  const token    = user?.token;
  const userId   = user?.emp_id || user?.id || user?.email || "default";
  const navigate = useNavigate();

  /* ── Panel open state ── */
  const [xferOpen,       setXferOpen]       = useState(true);
  const [sumOpen,        setSumOpen]        = useState(true);
  const [detailOpen,     setDetailOpen]     = useState(true);
  const [monthSumOpen,   setMonthSumOpen]   = useState(true);
  const [addonSumOpen,   setAddonSumOpen]   = useState(true);
  const [vendorSumOpen,  setVendorSumOpen]  = useState(true);
  const [exclSumOpen,    setExclSumOpen]    = useState(true);

  /* ── Detail grid: GST filter toggle ── */
  // 'all' | 'applicable' | 'excluded'
  const [gstFilter, setGstFilter] = useState("all");

  /* ── Vendor pagination ── */
  const [vendorPage,     setVendorPage]     = useState(1);
  const [vendorPageSize, setVendorPageSize] = useState(25);

  /* ── Action API state ── */
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg,     setActionMsg]     = useState({ type:"", text:"" }); // type: success|error

  /* ── Transfer panel ── */
  const [period,  setPeriod]  = useState("");
  const [entity,  setEntity]  = useState("");
  const [loading,    setLoading]    = useState(false);
  const [stepMsg,    setStepMsg]    = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg,   setErrorMsg]   = useState("");
  const [insertedCount, setInsertedCount] = useState(null);

  /* ── Summary grid ── */
  const [summaryRows,  setSummaryRows]  = useState([]);
  const [filteredSum,  setFilteredSum]  = useState([]);
  const [filterSumPeriod, setFilterSumPeriod] = useState("");
  const [filterSumEntity, setFilterSumEntity] = useState("");
  const [sumPage,     setSumPage]     = useState(1);
  const [sumPageSize, setSumPageSize] = useState(10);

  /* ── Detail grid data ── */
  const [detailRows,    setDetailRows]    = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState("");
  const [detailPeriod,  setDetailPeriod]  = useState("");
  const [detailEntity,  setDetailEntity]  = useState("");

  /* ── Detail grid: column filters ── */
  const [colFilters,    setColFilters]    = useState({}); // { key: Set<string> }  — for text/flag/amount
  const [dateFilters,   setDateFilters]   = useState({}); // { key: {op,val1,val2} } — for date cols
  const [openFilterCol, setOpenFilterCol] = useState(null);
  const [filterDropPos, setFilterDropPos] = useState({ top:0, left:0 });
  const [filterSearch,  setFilterSearch]  = useState("");

  /* ── Detail grid: column visibility + order + sort
        Initialised instantly from localStorage (no flicker).
        Server prefs are fetched on mount and override if found.       ── */
  const localPrefs                    = readLocalPrefs(userId);
  const [colVis,      setColVis]      = useState(localPrefs.vis);
  const [colOrder,    setColOrder]    = useState(localPrefs.order);
  const [sortOrder,   setSortOrder]   = useState(localPrefs.sortOrder);

  // UI state for column panel
  const [colVisOpen,  setColVisOpen]  = useState(false);
  const [colVisPos,   setColVisPos]   = useState({ top:0, left:0 });
  const [colVisSearch,setColVisSearch]= useState("");

  // Prefs load/save status: 'idle' | 'loading' | 'saving' | 'saved' | 'error'
  const [prefsSaveStatus, setPrefsSaveStatus] = useState("idle");
  const prefsInitialized = useRef(false); // prevent save firing during initial load

  /* ── Detail grid: selection & pagination ── */
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [detailPage,  setDetailPage]  = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(50);

  const filterDropRef = useRef();
  const colVisPanelRef = useRef();

  const monthOptions = [
    "JAN-2025","FEB-2025","MAR-2025","APR-2025","MAY-2025","JUN-2025",
    "JUL-2025","AUG-2025","SEP-2025","OCT-2025","NOV-2025","DEC-2025",
  ];
  const entityOptions = [
    { code:"SE", name:"Singhal Steel & Power Pvt Ltd" },
    { code:"SS", name:"Shyam ISAP Pvt Ltd" },
  ];

  /* ── Close dropdowns on outside click ── */
  useEffect(() => {
    const h = (e) => {
      if (filterDropRef.current && !filterDropRef.current.contains(e.target)) {
        setOpenFilterCol(null);
      }
      if (colVisPanelRef.current && !colVisPanelRef.current.contains(e.target)) {
        setColVisOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* ── LOAD prefs from server on mount ──────────────────────────────
     Flow: localStorage already applied (instant) → server fetch →
     if server has prefs → override state → mark initialized
     if server 404/error → use localStorage as-is → mark initialized
  ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchServerPrefs = async () => {
      setPrefsSaveStatus("loading");
      try {
        const res = await axios.post(
          "/api/users/getUserScreenPrefs",
          { screen_key: SCREEN_KEY },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.prefs) {
          const merged = mergePrefs(res.data.prefs);
          // Override state with server values (cross-device source of truth)
          setColVis(merged.vis);
          setColOrder(merged.order);
          setSortOrder(merged.sortOrder);
          // Also update localStorage cache to match server
          writeLocalPrefs(userId, merged);
        }
      } catch {
        // Server unavailable or 404 — keep localStorage values already in state
      } finally {
        setPrefsSaveStatus("idle");
        prefsInitialized.current = true;
      }
    };
    if (token) fetchServerPrefs();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── AUTO-SAVE prefs to server + localStorage (debounced 1.5s) ────
     Only fires after initial load is complete (prefsInitialized flag)
     so we don't overwrite server data with stale localStorage on mount.
  ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!prefsInitialized.current) return;
    const prefs = { vis: colVis, order: colOrder, sortOrder };
    // Write to localStorage immediately (instant local cache)
    writeLocalPrefs(userId, prefs);
    // Debounce server save
    setPrefsSaveStatus("saving");
    const timer = setTimeout(async () => {
      try {
        await axios.post(
          "/api/users/saveUserScreenPrefs",
          { screen_key: SCREEN_KEY, prefs },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPrefsSaveStatus("saved");
        // Hide "saved" indicator after 2s
        setTimeout(() => setPrefsSaveStatus("idle"), 2000);
      } catch {
        setPrefsSaveStatus("error");
        setTimeout(() => setPrefsSaveStatus("idle"), 3000);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [colVis, colOrder, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ════════════════════════════════════════
     SUMMARY GRID LOGIC
  ════════════════════════════════════════ */
  const loadSummary = async () => {
    try {
      const res = await axios.post("/api/gst2b/getErpDataStatus", {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(res.data.data) ? res.data.data : [];
      setSummaryRows(rows);
      setFilteredSum(rows);
    } catch (err) {
      console.error("Summary Load Error:", err);
    }
  };

  useEffect(() => { loadSummary(); }, []);

  useEffect(() => {
    let rows = [...summaryRows];
    if (filterSumPeriod) rows = rows.filter(r => (r.PERIOD || r.period) === filterSumPeriod);
    if (filterSumEntity) rows = rows.filter(r => (r.ENTITY_CODE || r.entity_code) === filterSumEntity);
    setFilteredSum(rows);
    setSumPage(1);
  }, [filterSumPeriod, filterSumEntity, summaryRows]);

  const sumTotalPages    = Math.ceil(filteredSum.length / sumPageSize);
  const sumPaginatedRows = filteredSum.slice((sumPage-1)*sumPageSize, sumPage*sumPageSize);

  /* ════════════════════════════════════════
     TRANSFER LOGIC
  ════════════════════════════════════════ */
  const handleProcess = async (reprocessMode) => {
    if (!period || !entity) { setErrorMsg("Please select Period and Entity."); return; }
    setLoading(true); setSuccessMsg(""); setErrorMsg(""); setInsertedCount(null);
    setStepMsg("Processing ERP data transfer...");
    try {
      const res = await axios.post("/api/gst2b/postERPPurchases",
        { month: period, entity_code: entity, reprocess: reprocessMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === "exists" || res.data.status === "no_records") {
        setErrorMsg(res.data.message);
      } else {
        setSuccessMsg(res.data.message || "Process completed successfully.");
        if (res.data.inserted) setInsertedCount(res.data.inserted);
        await loadSummary();
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "An error occurred during processing.");
    }
    setStepMsg(""); setLoading(false);
  };

  /* ════════════════════════════════════════
     DETAIL GRID LOGIC
  ════════════════════════════════════════ */
  const loadDetail = async () => {
    if (!detailPeriod || !detailEntity) {
      setDetailError("Select Period and Entity to load detail records.");
      return;
    }
    setDetailLoading(true); setDetailError(""); setDetailRows([]);
    setColFilters({}); setDateFilters({}); setSelectedIds(new Set());
    setDetailPage(1); setGstFilter("all"); setVendorPage(1);
    setActionMsg({ type:"", text:"" });
    try {
      const res = await axios.post("/api/gst2b/getErpDetailData",
        { month: detailPeriod, entity_code: detailEntity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Backend returns array directly: res.status(200).json(reconSummaryData)
      // Fallback also handles { data: [...] } shape if backend is changed later
      const rows = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
          ? res.data.data
          : [];
      setDetailRows(rows);
    } catch (err) {
      setDetailError(err.response?.data?.message || "Failed to load detail records.");
    }
    setDetailLoading(false);
  };

  /* ── Unique values per column (from full dataset) ── */
  const uniqueVals = useCallback((colKey) => {
    const counts = {};
    detailRows.forEach(r => {
      const v = colKey2str(r, colKey);
      counts[v] = (counts[v] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]));
  }, [detailRows]);

  /* ── Filtered detail rows (gstFilter + checkbox + date range) ── */
  const filteredDetailRows = useMemo(() => {
    return detailRows.filter(row => {
      // 0. GST Applicable / Excluded quick-filter
      const flag = row.EXCLUDED_FLAG;
      const isExcluded = flag === "Y" || flag === "y";
      if (gstFilter === "applicable" &&  isExcluded) return false;
      if (gstFilter === "excluded"   && !isExcluded) return false;
      const passesCheckbox = Object.entries(colFilters).every(([key, selSet]) => {
        if (!selSet) return true;
        if (selSet.size === 0) return false;
        return selSet.has(colKey2str(row, key));
      });
      if (!passesCheckbox) return false;

      // 2. Date range / comparison filters
      const passesDate = Object.entries(dateFilters).every(([key, df]) => {
        if (!df || !df.op || !df.val1) return true; // incomplete → no filter
        const raw = row[key];
        if (!raw) return false; // no date value → exclude
        const rowD = new Date(raw);
        if (isNaN(rowD)) return false;
        const d1 = new Date(df.val1);
        if (isNaN(d1)) return true;
        // Normalise to date only (strip time) for comparisons
        const rd = new Date(rowD.getFullYear(), rowD.getMonth(), rowD.getDate());
        const d1n = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
        switch (df.op) {
          case "eq":      return rd.getTime() === d1n.getTime();
          case "gte":     return rd >= d1n;
          case "gt":      return rd > d1n;
          case "lte":     return rd <= d1n;
          case "lt":      return rd < d1n;
          case "between": {
            if (!df.val2) return rd >= d1n;
            const d2 = new Date(df.val2);
            if (isNaN(d2)) return rd >= d1n;
            const d2n = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
            return rd >= d1n && rd <= d2n;
          }
          default: return true;
        }
      });
      return passesDate;
    });
  }, [detailRows, colFilters, dateFilters, gstFilter]);

  /* ── Sorted + filtered rows ── */
  const sortedFilteredRows = useMemo(() => {
    if (sortOrder.length === 0) return filteredDetailRows;
    return [...filteredDetailRows].sort((a, b) => {
      for (const { key, dir } of sortOrder) {
        const col = COLUMNS.find(c => c.key === key);
        const va  = a[key], vb = b[key];
        let cmp   = 0;
        if (col?.type === "amount") {
          cmp = (parseFloat(va) || 0) - (parseFloat(vb) || 0);
        } else if (col?.type === "date") {
          cmp = new Date(va || 0) - new Date(vb || 0);
        } else {
          cmp = String(va ?? "").localeCompare(String(vb ?? ""), "en-IN", { numeric: true });
        }
        if (cmp !== 0) return dir === "asc" ? cmp : -cmp;
      }
      return 0;
    });
  }, [filteredDetailRows, sortOrder]);

  /* ── Detail pagination ── */
  const detailTotalPages = Math.ceil(sortedFilteredRows.length / detailPageSize);
  const detailPaged      = sortedFilteredRows.slice(
    (detailPage - 1) * detailPageSize, detailPage * detailPageSize
  );

  /* ── Summary totals from filtered rows ── */
  const detailSummary = useMemo(() => {
    const t = {};
    AMOUNT_KEYS.forEach(k => { t[k] = 0; });
    filteredDetailRows.forEach(row => {
      AMOUNT_KEYS.forEach(k => { t[k] += parseFloat(row[k]) || 0; });
    });
    return t;
  }, [filteredDetailRows]);

  /* ── Helper: sum amounts for a group of rows ── */
  const sumAmounts = (rows) => {
    const t = { count: rows.length, ASSESSABLE_VALUE:0, IGST_AMOUNT:0, CGST_AMOUNT:0,
                SGST_AMOUNT:0, CESS_AMOUNT:0, TOTAL_TAX_VALUE:0, BILL_AMOUNT:0 };
    rows.forEach(r => {
      t.ASSESSABLE_VALUE += parseFloat(r.ASSESSABLE_VALUE) || 0;
      t.IGST_AMOUNT      += parseFloat(r.IGST_AMOUNT)      || 0;
      t.CGST_AMOUNT      += parseFloat(r.CGST_AMOUNT)      || 0;
      t.SGST_AMOUNT      += parseFloat(r.SGST_AMOUNT)      || 0;
      t.CESS_AMOUNT      += parseFloat(r.CESS_AMOUNT)      || 0;
      t.TOTAL_TAX_VALUE  += parseFloat(r.TOTAL_TAX_VALUE)  || 0;
      t.BILL_AMOUNT      += parseFloat(r.BILL_AMOUNT)      || 0;
    });
    return t;
  };

  /* ── PANEL 4: Monthly Summary (group by PARTY_BILL_DATE → month) ── */
  const monthlySummary = useMemo(() => {
    const map = {};
    filteredDetailRows.forEach(row => {
      const d   = row.PARTY_BILL_DATE ? new Date(row.PARTY_BILL_DATE) : null;
      const key = d && !isNaN(d)
        ? `${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}` // sortable
        : "99-9999";
      const label = d && !isNaN(d)
        ? d.toLocaleDateString("en-IN", { month:"short", year:"numeric" })
        : "(No Date)";
      if (!map[key]) map[key] = { _key: key, month: label, rows: [] };
      map[key].rows.push(row);
    });
    return Object.values(map)
      .sort((a, b) => a._key.localeCompare(b._key))
      .map(g => ({ month: g.month, ...sumAmounts(g.rows) }));
  }, [filteredDetailRows]);

  /* ── PANEL 5: Addon Summary (group by ADDON_CODE + ADDON_NAME) ── */
  const addonSummary = useMemo(() => {
    const map = {};
    filteredDetailRows.forEach(row => {
      const code = row.ADDON_CODE || "(blank)";
      const name = row.ADDON_NAME || "(blank)";
      const key  = `${code}||${name}`;
      if (!map[key]) map[key] = { ADDON_CODE: code, ADDON_NAME: name, rows: [] };
      map[key].rows.push(row);
    });
    return Object.values(map)
      .sort((a, b) => a.ADDON_CODE.localeCompare(b.ADDON_CODE))
      .map(g => ({ ADDON_CODE: g.ADDON_CODE, ADDON_NAME: g.ADDON_NAME, ...sumAmounts(g.rows) }));
  }, [filteredDetailRows]);

  /* ── PANEL 6: Vendor Pivot (rows = vendor, columns = months, values = BILL_AMOUNT) ── */
  const vendorPivot = useMemo(() => {
    const monthMap  = {}; // sortKey → label
    const vendorMap = {};
    filteredDetailRows.forEach(row => {
      const d      = row.PARTY_BILL_DATE ? new Date(row.PARTY_BILL_DATE) : null;
      const mKey   = d && !isNaN(d) ? `${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}` : "99-9999";
      const mLabel = d && !isNaN(d) ? d.toLocaleDateString("en-IN", { month:"short", year:"numeric" }) : "(No Date)";
      monthMap[mKey] = mLabel;
      const vKey   = `${row.PARTY_CODE || ""}||${row.PARTY_NAME || ""}`;
      if (!vendorMap[vKey]) {
        vendorMap[vKey] = {
          PARTY_CODE: row.PARTY_CODE || "",
          PARTY_NAME: row.PARTY_NAME || "",
          months: {}, total: 0,
        };
      }
      const amt = parseFloat(row.BILL_AMOUNT) || 0;
      vendorMap[vKey].months[mKey]  = (vendorMap[vKey].months[mKey] || 0) + amt;
      vendorMap[vKey].total        += amt;
    });
    const months  = Object.entries(monthMap)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, v]) => ({ key: k, label: v }));
    const vendors = Object.values(vendorMap)
      .sort((a, b) => (a.PARTY_NAME || "").localeCompare(b.PARTY_NAME || ""));
    return { months, vendors };
  }, [filteredDetailRows]);

  /* ── PANEL 7: Exclusion Summary (Included vs Excluded split) ── */
  const exclusionSummary = useMemo(() => {
    // Always derived from ALL detailRows (unaffected by gstFilter)
    // so the summary shows the true split regardless of current filter
    const included = detailRows.filter(r => r.EXCLUDED_FLAG !== "Y" && r.EXCLUDED_FLAG !== "y");
    const excluded = detailRows.filter(r => r.EXCLUDED_FLAG === "Y" || r.EXCLUDED_FLAG === "y");
    return {
      included: sumAmounts(included),
      excluded: sumAmounts(excluded),
      total:    sumAmounts(detailRows),
    };
  }, [detailRows]);

  /* ── Vendor pagination ── */
  const vendorTotalPages = Math.ceil(vendorPivot.vendors.length / vendorPageSize);
  const vendorPaged      = vendorPivot.vendors.slice(
    (vendorPage - 1) * vendorPageSize, vendorPage * vendorPageSize
  );

  /* ── Visible columns in user-defined order ── */
  const visibleCols = useMemo(() =>
    colOrder
      .map(key => COLUMNS.find(c => c.key === key))
      .filter(c => c && colVis[c.key] !== false),
    [colVis, colOrder]
  );

  /* ── Active filter helpers ── */
  const filterCount    = (key) => {
    if (key in dateFilters && dateFilters[key]?.val1) return "D"; // date filter active
    if (!(key in colFilters)) return 0;
    return colFilters[key].size;
  };
  const isFilterActive = (key) =>
    (key in colFilters) || (key in dateFilters && !!dateFilters[key]?.val1);

  /* ── Clear one column filter (checkbox or date) ── */
  const clearFilter = (colKey) => {
    setColFilters(prev => { const n = { ...prev }; delete n[colKey]; return n; });
    setDateFilters(prev => { const n = { ...prev }; delete n[colKey]; return n; });
    setDetailPage(1);
  };

  /* ── Clear ALL filters ── */
  const clearAllFilters = () => {
    setColFilters({});
    setDateFilters({});
    setDetailPage(1);
  };
  const activeFilterCount =
    Object.keys(colFilters).length +
    Object.values(dateFilters).filter(df => df?.val1).length;

  /* ── Apply / update a date filter ── */
  const applyDateFilter = (colKey, op, val1, val2 = "") => {
    setDateFilters(prev => ({ ...prev, [colKey]: { op, val1, val2 } }));
    setDetailPage(1);
  };
  const openFilter = (e, colKey) => {
    e.stopPropagation();
    if (openFilterCol === colKey) { setOpenFilterCol(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterDropPos({ top: rect.bottom + 1, left: rect.left });
    setOpenFilterCol(colKey);
    setFilterSearch("");
  };

  /* ── Toggle a single value in column filter ── */
  const toggleFilterVal = (colKey, val) => {
    setColFilters(prev => {
      const all = uniqueVals(colKey).map(([v]) => v);
      // If no filter key → start from full set (all were shown)
      // If filter key exists (even empty Set) → start from current set
      const cur = (colKey in prev) ? new Set(prev[colKey]) : new Set(all);
      if (cur.has(val)) cur.delete(val); else cur.add(val);
      // If all values selected → remove filter key entirely (back to "all")
      if (cur.size === all.length) {
        const next = { ...prev }; delete next[colKey]; return next;
      }
      return { ...prev, [colKey]: cur };
    });
    setDetailPage(1);
  };

  /* ── Select All toggle for a column filter ── */
  const toggleFilterAll = (colKey) => {
    setColFilters(prev => {
      const next = { ...prev };
      if (next[colKey]) {
        // Filter exists (some or none selected) → remove filter = select all
        delete next[colKey];
      } else {
        // No filter (all selected) → set empty Set = deselect all
        next[colKey] = new Set();
      }
      return next;
    });
    setDetailPage(1);
  };

  /* ── Is value selected in filter? ── */
  const isValSelected = (colKey, val) => {
    const s = colFilters[colKey];
    if (!s) return true; // no filter = all selected
    return s.has(val);
  };

  /* ── Filter dropdown content ── */
  const FilterDropdown = () => {
    const selectAllRef = useRef(null);

    const vals      = openFilterCol ? uniqueVals(openFilterCol) : [];
    const col       = openFilterCol ? COLUMNS.find(c => c.key === openFilterCol) : null;
    const isDateCol = col?.type === "date";
    const shown     = openFilterCol && filterSearch && !isDateCol
      ? vals.filter(([v]) => v.toLowerCase().includes(filterSearch.toLowerCase()))
      : vals;

    const filterSet    = openFilterCol ? colFilters[openFilterCol] : undefined;
    const allSelected  = openFilterCol ? !(openFilterCol in colFilters) : false;
    const noneSelected = filterSet && filterSet.size === 0;
    const someSelected = filterSet && filterSet.size > 0 && filterSet.size < vals.length;

    // Current date filter values
    const existingDF  = openFilterCol ? (dateFilters[openFilterCol] || { op:"gte", val1:"", val2:"" }) : { op:"gte", val1:"", val2:"" };
    const [dfOp,   setDfOp]   = useState(existingDF.op   || "gte");
    const [dfVal1, setDfVal1] = useState(existingDF.val1 || "");
    const [dfVal2, setDfVal2] = useState(existingDF.val2 || "");

    useEffect(() => {
      if (selectAllRef.current) {
        selectAllRef.current.indeterminate = !!someSelected;
      }
    }, [someSelected]);

    if (!openFilterCol) return null;

    const DATE_OPS = [
      { val:"eq",      label:"= Equal to"         },
      { val:"gte",     label:"≥ On or After"       },
      { val:"gt",      label:"> After"             },
      { val:"lte",     label:"≤ On or Before"      },
      { val:"lt",      label:"< Before"            },
      { val:"between", label:"↔ Between"           },
    ];

    return (
      <div
        ref={filterDropRef}
        className={styles.filterDropdown}
        style={{ top: filterDropPos.top, left: filterDropPos.left,
                 width: isDateCol ? 250 : 220 }}
      >
        {/* Header */}
        <div className={styles.filterDropHeader}>
          <span className={styles.filterDropTitle}>
            {isDateCol ? "📅 " : ""}{col?.label || openFilterCol}
          </span>
          <button className={styles.filterDropClose}
            onClick={() => setOpenFilterCol(null)}>✕</button>
        </div>

        {/* ── DATE COLUMN: operator + date inputs ── */}
        {isDateCol ? (
          <div style={{ padding:"8px 10px", display:"flex", flexDirection:"column", gap:6 }}>
            {/* Operator select */}
            <div>
              <div style={{ fontSize:10, color:"#666", marginBottom:3 }}>Condition</div>
              <select
                className={styles.select}
                style={{ width:"100%", height:24, fontSize:11 }}
                value={dfOp}
                onChange={e => setDfOp(e.target.value)}
              >
                {DATE_OPS.map(o => (
                  <option key={o.val} value={o.val}>{o.label}</option>
                ))}
              </select>
            </div>
            {/* Date 1 */}
            <div>
              <div style={{ fontSize:10, color:"#666", marginBottom:3 }}>
                {dfOp === "between" ? "From Date" : "Date"}
              </div>
              <input
                type="date"
                className={styles.input}
                style={{ width:"100%", height:24, fontSize:11 }}
                value={dfVal1}
                onChange={e => setDfVal1(e.target.value)}
              />
            </div>
            {/* Date 2 — only for "between" */}
            {dfOp === "between" && (
              <div>
                <div style={{ fontSize:10, color:"#666", marginBottom:3 }}>To Date</div>
                <input
                  type="date"
                  className={styles.input}
                  style={{ width:"100%", height:24, fontSize:11 }}
                  value={dfVal2}
                  onChange={e => setDfVal2(e.target.value)}
                />
              </div>
            )}
            {/* Current active filter badge */}
            {dateFilters[openFilterCol]?.val1 && (
              <div style={{ fontSize:10, color:"#1b6ca8", background:"#eaf2fa",
                            padding:"3px 6px", borderRadius:2, border:"1px solid #bfdbfe" }}>
                Active: {DATE_OPS.find(o => o.val === dateFilters[openFilterCol].op)?.label} {" "}
                {fmtDate(dateFilters[openFilterCol].val1)}
                {dateFilters[openFilterCol].op === "between" && dateFilters[openFilterCol].val2 &&
                  ` → ${fmtDate(dateFilters[openFilterCol].val2)}`}
              </div>
            )}
          </div>
        ) : (
          /* ── NON-DATE COLUMN: search + checkbox list ── */
          <>
            <div className={styles.filterDropSearch}>
              <input
                autoFocus
                placeholder="Search values..."
                value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </div>
            <div className={styles.filterDropList}>
              <div className={`${styles.filterDropItem} ${styles.filterDropItemAll}`}
                onClick={() => toggleFilterAll(openFilterCol)}>
                <input ref={selectAllRef} type="checkbox" readOnly
                  checked={allSelected} onChange={() => {}} />
                <span>(Select All)</span>
                <span className={styles.filterDropCount}>{vals.length}</span>
              </div>
              {shown.map(([v, cnt]) => (
                <div key={v} className={styles.filterDropItem}
                  onClick={() => toggleFilterVal(openFilterCol, v)}>
                  <input type="checkbox" readOnly
                    checked={isValSelected(openFilterCol, v)} onChange={() => {}} />
                  <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis" }}>{v}</span>
                  <span className={styles.filterDropCount}>{cnt}</span>
                </div>
              ))}
              {shown.length === 0 && (
                <div style={{ padding:"8px 10px", color:"#888", fontSize:10 }}>
                  No matching values
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className={styles.filterDropFooter}>
          {!isDateCol && (
            <span style={{ fontSize:10, color:"#888", marginRight:"auto" }}>
              {allSelected ? `All ${vals.length}` : noneSelected ? "None" : `${filterSet.size}/${vals.length}`} selected
            </span>
          )}
          <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
            onClick={() => clearFilter(openFilterCol)}>
            Reset
          </button>
          {isDateCol ? (
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}
              onClick={() => {
                if (dfVal1) applyDateFilter(openFilterCol, dfOp, dfVal1, dfVal2);
                else clearFilter(openFilterCol);
                setOpenFilterCol(null);
              }}>
              Apply
            </button>
          ) : (
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}
              onClick={() => setOpenFilterCol(null)}>
              OK
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ── Column Visibility Panel ── */
  const openColVisPanel = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setColVisPos({ top: rect.bottom + 1, left: Math.max(0, rect.right - 240) });
    setColVisOpen(true);
    setColVisSearch("");
  };

  const ColVisPanel = () => {
    const dragKeyRef  = useRef(null);  // key being dragged
    const dragOverRef = useRef(null);  // key currently dragged over

    if (!colVisOpen) return null;

    // When searching, show filtered list but disable drag (order not meaningful during search)
    const isSearching = !!colVisSearch;
    const displayList = isSearching
      ? COLUMNS.filter(c => c.label.toLowerCase().includes(colVisSearch.toLowerCase()))
      : colOrder.map(key => COLUMNS.find(c => c.key === key)).filter(Boolean);

    const handleDragStart = (e, key) => {
      dragKeyRef.current = key;
      e.dataTransfer.effectAllowed = "move";
    };
    const handleDragOver = (e, key) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      dragOverRef.current = key;
    };
    const handleDrop = (e, targetKey) => {
      e.preventDefault();
      const fromKey = dragKeyRef.current;
      if (!fromKey || fromKey === targetKey) return;
      setColOrder(prev => {
        const arr  = [...prev];
        const from = arr.indexOf(fromKey);
        const to   = arr.indexOf(targetKey);
        if (from === -1 || to === -1) return prev;
        arr.splice(from, 1);
        arr.splice(to, 0, fromKey);
        return arr;
      });
      dragKeyRef.current  = null;
      dragOverRef.current = null;
    };
    const handleDragEnd = () => {
      dragKeyRef.current  = null;
      dragOverRef.current = null;
    };

    return (
      <div ref={colVisPanelRef} className={styles.colVisPanel}
        style={{ top: colVisPos.top, left: colVisPos.left }}>

        <div className={styles.colVisPanelHeader}>
          <span>Columns — Show / Hide / Reorder</span>
          <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
            onClick={() => {
              setColVis({ ...DEFAULT_VIS });
              setColOrder([...DEFAULT_ORDER]);
              setSortOrder([]);
            }}>
            Reset
          </button>
        </div>

        {/* Search */}
        <div style={{ padding:"4px 8px", borderBottom:"1px solid #d0d0d0" }}>
          <input
            className={styles.input}
            style={{ width:"100%", height:22, fontSize:10 }}
            placeholder="Search columns..."
            value={colVisSearch}
            onChange={e => setColVisSearch(e.target.value)}
          />
        </div>

        {/* Instruction */}
        {!isSearching && (
          <div style={{ padding:"3px 10px", fontSize:9, color:"#999", borderBottom:"1px solid #eee", background:"#fafafa" }}>
            ⠿ Drag rows to reorder columns
          </div>
        )}

        {/* Column list */}
        <div className={styles.colVisList}>
          {displayList.map(c => (
            <div
              key={c.key}
              className={styles.colVisItem}
              draggable={!isSearching}
              onDragStart={e => handleDragStart(e, c.key)}
              onDragOver={e  => handleDragOver(e, c.key)}
              onDrop={e      => handleDrop(e, c.key)}
              onDragEnd={handleDragEnd}
              style={{ cursor: isSearching ? "default" : "grab" }}
            >
              {/* Drag handle */}
              {!isSearching && (
                <span className={styles.dragHandle} title="Drag to reorder">⠿</span>
              )}
              {/* Visibility checkbox */}
              <input
                type="checkbox"
                readOnly
                checked={!!colVis[c.key]}
                onChange={() => {}}
                onClick={e => {
                  e.stopPropagation();
                  setColVis(prev => ({ ...prev, [c.key]: !prev[c.key] }));
                }}
                style={{ cursor:"pointer" }}
              />
              <span
                style={{ flex:1, opacity: colVis[c.key] ? 1 : 0.45 }}
                onClick={() => setColVis(prev => ({ ...prev, [c.key]: !prev[c.key] }))}
              >
                {c.label}
              </span>
              <span style={{ marginLeft:"auto", fontSize:9, color:"#bbb" }}>{c.key}</span>
            </div>
          ))}
        </div>

        <div className={styles.colVisFooter}>
          <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
            onClick={() => setColVis(Object.fromEntries(COLUMNS.map(c => [c.key, true])))}>
            Show All
          </button>
          <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
            onClick={() => setColVis(Object.fromEntries(COLUMNS.map(c => [c.key, false])))}>
            Hide All
          </button>
          <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}
            onClick={() => setColVisOpen(false)}>
            Close
          </button>
        </div>
      </div>
    );
  };

  /* ── Row selection ── */
  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    const pageIds = detailPaged.map(r => r.ID);
    const allSel  = pageIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const n = new Set(prev);
      pageIds.forEach(id => allSel ? n.delete(id) : n.add(id));
      return n;
    });
  };

  /* ── Action handler ── */
  const handleAction = async (actionId) => {
    const selRows = filteredDetailRows.filter(r => selectedIds.has(r.ID));
    const selIds  = selRows.map(r => r.ID);

    switch (actionId) {

      // ── Quick-filter actions (no API) ──
      case "show_all":
        setGstFilter("all");
        break;
      case "show_applicable":
        setGstFilter("applicable");
        break;
      case "show_excluded":
        setGstFilter("excluded");
        break;

      // ── Exclude selected rows (API) ──
      case "exclude_selected": {
        if (selIds.length === 0) return;
        setActionLoading(true);
        setActionMsg({ type:"", text:"" });
        try {
          const res = await axios.post(
            "/api/gst2b/updateGstExclusionFlag",
            { ids: selIds, action: "exclude" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setActionMsg({ type:"success", text: res.data.message });
          // Update local state immediately — no reload needed
          setDetailRows(prev => prev.map(r =>
            selIds.includes(r.ID)
              ? { ...r, EXCLUDED_FLAG: "Y", EXCLUDED_BY: userId }
              : r
          ));
          setSelectedIds(new Set());
        } catch (err) {
          setActionMsg({ type:"error", text: err.response?.data?.message || "Exclude failed." });
        }
        setActionLoading(false);
        break;
      }

      // ── Include selected rows (reverse exclusion, API) ──
      case "include_selected": {
        if (selIds.length === 0) return;
        setActionLoading(true);
        setActionMsg({ type:"", text:"" });
        try {
          const res = await axios.post(
            "/api/gst2b/updateGstExclusionFlag",
            { ids: selIds, action: "include" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setActionMsg({ type:"success", text: res.data.message });
          setDetailRows(prev => prev.map(r =>
            selIds.includes(r.ID)
              ? { ...r, EXCLUDED_FLAG: null, EXCLUDED_BY: null, EXCLUDED_ON: null }
              : r
          ));
          setSelectedIds(new Set());
        } catch (err) {
          setActionMsg({ type:"error", text: err.response?.data?.message || "Include failed." });
        }
        setActionLoading(false);
        break;
      }

      case "mark_rcm":
        setActionMsg({ type:"info", text:`Feature: Mark ${selRows.length} rows as RCM — wire to your API` });
        break;
      case "unmark_rcm":
        setActionMsg({ type:"info", text:`Feature: Unmark RCM on ${selRows.length} rows — wire to your API` });
        break;
      case "export_csv":
        exportCSV(filteredDetailRows, "erp_detail_filtered.csv");
        break;
      case "export_sel_csv":
        exportCSV(selRows, "erp_detail_selected.csv");
        break;
      default: break;
    }
  };

  /* ── CSV Export ── */
  const exportCSV = (rows, filename) => {
    const header = visibleCols.map(c => c.label).join(",");
    const body   = rows.map(r =>
      visibleCols.map(c => {
        const v = cellVal(c, r);
        return `"${String(v).replace(/"/g,'""')}"`;
      }).join(",")
    );
    const csv  = [header, ...body].join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement("a"), { href:url, download:filename });
    a.click(); URL.revokeObjectURL(url);
  };

  /* ── Column sort handler ── */
  // Plain click → single-column sort (ASC → DESC → off)
  // Shift+Click → add/toggle in multi-sort list
  const handleColSort = (e, colKey) => {
    e.stopPropagation();
    setSortOrder(prev => {
      const existing = prev.find(s => s.key === colKey);
      if (e.shiftKey) {
        // Multi-sort mode
        if (!existing)               return [...prev, { key: colKey, dir: "asc" }];
        if (existing.dir === "asc")  return prev.map(s => s.key === colKey ? { ...s, dir: "desc" } : s);
        return prev.filter(s => s.key !== colKey); // 3rd click removes
      } else {
        // Single-sort mode — replaces everything
        if (!existing)               return [{ key: colKey, dir: "asc" }];
        if (existing.dir === "asc")  return [{ key: colKey, dir: "desc" }];
        return [];  // 3rd click clears all sort
      }
    });
    setDetailPage(1);
  };

  /* ════════════════════════════════════════
     EARLY RETURN — not logged in
  ════════════════════════════════════════ */
  if (!token) {
    return (
      <div className={styles.root}>
        <div className={styles.loginWall}>
          <div>You must be logged in to access this transaction.</div>
          <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════
     RENDER
  ════════════════════════════════════════ */
  return (
    <div className={styles.root}>

      {/* ── Shell Bar ── */}
      <div className={styles.shell}>
        <div className={styles.shellLogo}>GST Portal</div>
        <div className={styles.shellTitle}>GSTR-2B Reconciliation System</div>
        <div className={styles.shellRight}>
          <span>User: {user?.name || user?.email || "—"}</span>
          <span>|</span>
          <span>{new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</span>
        </div>
      </div>

      <div className={styles.page}>

        {/* ── Page Title Bar ── */}
        <div className={styles.titleBar}>
          <h1>Transfer ERP Purchase Data — GSTR-2B</h1>
          <div className={styles.breadcrumb}>
            <span>Home</span> &rsaquo; GST Modules &rsaquo; <span>GSTR-2B</span> &rsaquo; Transfer ERP Data
          </div>
        </div>

        {/* ══════════════════════════════════════════
            PANEL 1 — TRANSFER PARAMETERS
        ══════════════════════════════════════════ */}
        <Panel title="Transfer Parameters" open={xferOpen} onToggle={() => setXferOpen(o => !o)}>
          {/* Toolbar */}
          <div className={styles.toolbar}>
            <button className={`${styles.btn} ${styles.btnPrimary}`} disabled={loading}
              onClick={() => handleProcess("N")}>
              ▶ Execute Transfer
            </button>
            <button className={`${styles.btn} ${styles.btnWarn}`} disabled={loading}
              onClick={() => handleProcess("Y")}>
              ↻ Re-Transfer (Overwrite)
            </button>
            <div className={styles.toolbarSep} />
            <button className={`${styles.btn} ${styles.btnDefault}`}
              onClick={() => { setPeriod(""); setEntity(""); setSuccessMsg(""); setErrorMsg(""); setInsertedCount(null); }}>
              ✕ Clear
            </button>
          </div>

          {/* Form */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelReq}`}>Period</label>
              <select className={styles.select} style={{ minWidth:140 }}
                value={period} disabled={loading}
                onChange={e => setPeriod(e.target.value)}>
                <option value="">-- Select --</option>
                {monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelReq}`}>Entity</label>
              <select className={styles.select} style={{ minWidth:260 }}
                value={entity} disabled={loading}
                onChange={e => setEntity(e.target.value)}>
                <option value="">-- Select --</option>
                {entityOptions.map(e => (
                  <option key={e.code} value={e.code}>{e.code} — {e.name}</option>
                ))}
              </select>
            </div>
          </div>

          {loading && <div className={styles.processing}><div className={styles.spinner} /><span>{stepMsg}</span></div>}
          {successMsg && <div className={`${styles.msg} ${styles.msgSuccess}`}><span className={styles.msgIcon}>✓</span>{successMsg}</div>}
          {errorMsg   && <div className={`${styles.msg} ${styles.msgError}`}><span className={styles.msgIcon}>✕</span>{errorMsg}</div>}

          {insertedCount !== null && (
            <div className={styles.statStrip}>
              <div className={styles.statItem}><span className={styles.statLbl}>Records Inserted</span><span className={styles.statVal}>{insertedCount.toLocaleString("en-IN")}</span></div>
              <div className={styles.statItem}><span className={styles.statLbl}>Period</span><span className={styles.statVal} style={{ fontSize:13 }}>{period}</span></div>
              <div className={styles.statItem}><span className={styles.statLbl}>Entity</span><span className={styles.statVal} style={{ fontSize:13 }}>{entity}</span></div>
            </div>
          )}
        </Panel>

        {/* ══════════════════════════════════════════
            PANEL 2 — SUMMARY GRID
        ══════════════════════════════════════════ */}
        <Panel title="ERP Purchase Summary — Reconciliation Status"
          open={sumOpen} onToggle={() => setSumOpen(o => !o)}
          badge={filteredSum.length > 0 ? filteredSum.length : null}>

          {/* Toolbar */}
          <div className={styles.toolbar}>
            <button className={`${styles.btn} ${styles.btnDefault}`} onClick={loadSummary}>↺ Refresh</button>
            <div className={styles.toolbarSep} />
            <span className={styles.toolbarLabel}>Filter:</span>
            <select className={styles.select} style={{ minWidth:110 }}
              value={filterSumPeriod} onChange={e => setFilterSumPeriod(e.target.value)}>
              <option value="">All Periods</option>
              {monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select className={styles.select} style={{ minWidth:140 }}
              value={filterSumEntity} onChange={e => setFilterSumEntity(e.target.value)}>
              <option value="">All Entities</option>
              {entityOptions.map(e => <option key={e.code} value={e.code}>{e.code} — {e.name}</option>)}
            </select>
            <div className={styles.toolbarSep} />
            <span className={styles.toolbarLabel}>Rows/Pg:</span>
            <select className={styles.select} style={{ minWidth:60 }}
              value={sumPageSize} onChange={e => { setSumPageSize(parseInt(e.target.value)); setSumPage(1); }}>
              {[5,10,20,50].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className={`${styles.btn} ${styles.btnDefault}`}
              onClick={() => { setFilterSumPeriod(""); setFilterSumEntity(""); }}>
              ✕ Clear
            </button>
          </div>

          <div className={styles.tableInfo}>
            Showing <strong>{sumPaginatedRows.length}</strong> of <strong>{filteredSum.length}</strong> records
            {(filterSumPeriod || filterSumEntity) && <span style={{ color:"#1b6ca8", marginLeft:6 }}>[Filtered]</span>}
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table} style={{ minWidth:1640 }}>
              <thead>
                <tr className="grpRow">
                  <th className={styles.grpFix} rowSpan={2} style={{ textAlign:"left", minWidth:60 }}>Entity</th>
                  <th className={styles.grpFix} rowSpan={2} style={{ textAlign:"left", minWidth:90 }}>Period</th>
                  <th className={styles.grpTotal} colSpan={8}>TOTAL VALUES</th>
                  <th className={styles.grpRecon} colSpan={8}>RECONCILED VALUES</th>
                  <th className={styles.grpPend}  colSpan={8}>PENDING VALUES</th>
                </tr>
                <tr className="subRow">
                  {["Records","Assessable","IGST","CGST","SGST","CESS","Total Tax","Bill Amt",
                    "Records","Assessable","IGST","CGST","SGST","CESS","Total Tax","Bill Amt",
                    "Records","Assessable","IGST","CGST","SGST","CESS","Total Tax","Bill Amt",
                  ].map((h, i) => <th key={i}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {sumPaginatedRows.length === 0 ? (
                  <tr><td colSpan={26}><div className={styles.emptyState}>No records found.</div></td></tr>
                ) : (
                  sumPaginatedRows.map((row, idx) => {
                    const D = (a, b) => (parseFloat(a)||0) - (parseFloat(b)||0);
                    const ec = row.ENTITY_CODE || row.entity_code;
                    return (
                      <tr key={idx}>
                        <td><span className={ec==="SE" ? styles.chipSe : styles.chipSs}>{ec}</span></td>
                        <td><span className={styles.periodText}>{row.PERIOD || row.period}</span></td>
                        <td className={styles.tdTotal}>{fmt(row.TOTAL_CNT)}</td>
                        <td className={styles.tdTotal}>{fmt(row.TOTAL_ASSESSABLE_VALUE)}</td>
                        <td className={styles.tdTotal}>{fmt(row.TOTAL_IGST_AMT)}</td>
                        <td className={styles.tdTotal}>{fmt(row.TOTAL_CSG_AMT)}</td>
                        <td className={styles.tdTotal}>{fmt(row.TOTAL_SGST_AMT)}</td>
                        <td className={styles.tdTotal}>{fmt(row.TOTAL_CESS_AMT)}</td>
                        <td className={styles.tdTotal}>{fmt(row.TOTAL_TOTAL_TAX)}</td>
                        <td className={styles.tdTotal}>{fmt(row.TOTAL_BILL_AMT)}</td>
                        <td className={`${styles.tdRecon} ${styles.tdSep}`}>{fmt(row.RECON_CNT)}</td>
                        <td className={styles.tdRecon}>{fmt(row.RECON_ASSESSABLE_VALUE)}</td>
                        <td className={styles.tdRecon}>{fmt(row.RECON_IGST_AMT)}</td>
                        <td className={styles.tdRecon}>{fmt(row.RECON_CSG_AMT)}</td>
                        <td className={styles.tdRecon}>{fmt(row.RECON_SGST_AMT)}</td>
                        <td className={styles.tdRecon}>{fmt(row.RECON_CESS_AMT)}</td>
                        <td className={styles.tdRecon}>{fmt(row.RECON_TOTAL_TAX)}</td>
                        <td className={styles.tdRecon}>{fmt(row.RECON_BILL_AMT)}</td>
                        <td className={`${styles.tdPend} ${styles.tdSep}`}>{fmt(D(row.TOTAL_CNT,row.RECON_CNT))}</td>
                        <td className={styles.tdPend}>{fmt(D(row.TOTAL_ASSESSABLE_VALUE,row.RECON_ASSESSABLE_VALUE))}</td>
                        <td className={styles.tdPend}>{fmt(D(row.TOTAL_IGST_AMT,row.RECON_IGST_AMT))}</td>
                        <td className={styles.tdPend}>{fmt(D(row.TOTAL_CSG_AMT,row.RECON_CSG_AMT))}</td>
                        <td className={styles.tdPend}>{fmt(D(row.TOTAL_SGST_AMT,row.RECON_SGST_AMT))}</td>
                        <td className={styles.tdPend}>{fmt(D(row.TOTAL_CESS_AMT,row.RECON_CESS_AMT))}</td>
                        <td className={styles.tdPend}>{fmt(D(row.TOTAL_TOTAL_TAX,row.RECON_TOTAL_TAX))}</td>
                        <td className={styles.tdPend}>{fmt(D(row.TOTAL_BILL_AMT,row.RECON_BILL_AMT))}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <PagerBar
            page={sumPage} totalPages={sumTotalPages} total={filteredSum.length}
            pageSize={sumPageSize}
            onPage={setSumPage}
            onPageSize={n => { setSumPageSize(n); setSumPage(1); }}
          />
        </Panel>

        {/* ══════════════════════════════════════════
            PANEL 3 — DETAIL TRANSACTION GRID
        ══════════════════════════════════════════ */}
        <Panel
          title="Detail Transaction Records — ERP Purchase Ledger"
          open={detailOpen}
          onToggle={() => setDetailOpen(o => !o)}
          badge={filteredDetailRows.length > 0 ? `${filteredDetailRows.length.toLocaleString("en-IN")} records` : null}
        >
          {/* ── Load controls ── */}
          <div className={styles.toolbar}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} style={{ minWidth:"auto" }}>Period</label>
              <select className={styles.select} style={{ minWidth:120 }}
                value={detailPeriod} onChange={e => setDetailPeriod(e.target.value)}>
                <option value="">-- Select --</option>
                {monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} style={{ minWidth:"auto" }}>Entity</label>
              <select className={styles.select} style={{ minWidth:220 }}
                value={detailEntity} onChange={e => setDetailEntity(e.target.value)}>
                <option value="">-- Select --</option>
                {entityOptions.map(e => <option key={e.code} value={e.code}>{e.code} — {e.name}</option>)}
              </select>
            </div>
            <button className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={detailLoading} onClick={loadDetail}>
              {detailLoading ? "Loading..." : "▶ Load Records"}
            </button>
            <div className={styles.toolbarSep} />

            {/* Actions */}
            <ActionDropdown
              selectedCount={selectedIds.size}
              totalCount={filteredDetailRows.length}
              onAction={handleAction}
            />

            {/* Column Visibility */}
            <button className={`${styles.btn} ${styles.btnDefault}`} onClick={openColVisPanel}>
              ⊞ Columns ({visibleCols.length}/{COLUMNS.length})
            </button>

            {/* Prefs save status indicator */}
            {prefsSaveStatus === "loading" && (
              <span className={styles.prefsSaving}>
                <span className={styles.spinner} style={{ width:10, height:10, borderWidth:1.5 }} />
                Loading prefs…
              </span>
            )}
            {prefsSaveStatus === "saving" && (
              <span className={styles.prefsSaving}>
                <span className={styles.spinner} style={{ width:10, height:10, borderWidth:1.5 }} />
                Saving…
              </span>
            )}
            {prefsSaveStatus === "saved" && (
              <span className={styles.prefsSaved}>✓ Preferences saved</span>
            )}
            {prefsSaveStatus === "error" && (
              <span className={styles.prefsError}>⚠ Save failed (using local cache)</span>
            )}

            <div className={styles.toolbarSep} />

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button className={`${styles.btn} ${styles.btnDefault}`} onClick={clearAllFilters}>
                ✕ Clear Filters ({activeFilterCount})
              </button>
            )}
            {/* Clear Sort */}
            {sortOrder.length > 0 && (
              <button className={`${styles.btn} ${styles.btnDefault}`}
                onClick={() => setSortOrder([])}>
                ↕ Clear Sort
              </button>
            )}
          </div>

          {/* ── GST Filter Toggle Strip ── */}
          {detailRows.length > 0 && (
            <div className={styles.gstFilterStrip}>
              <span className={styles.toolbarLabel} style={{ marginRight:4 }}>GST Status:</span>
              {[
                { val:"all",        label:"☰ Show All",             clsActive: styles.gstBtnAll        },
                { val:"applicable", label:"✅ GST Applicable",      clsActive: styles.gstBtnApplicable },
                { val:"excluded",   label:"❌ Not Applicable",      clsActive: styles.gstBtnExcluded   },
              ].map(opt => (
                <button
                  key={opt.val}
                  className={`${styles.btn} ${styles.btnSm} ${gstFilter === opt.val ? opt.clsActive : styles.btnDefault}`}
                  onClick={() => { setGstFilter(opt.val); setDetailPage(1); }}
                >
                  {opt.label}
                </button>
              ))}
              <span style={{ fontSize:10, color:"#888", marginLeft:8 }}>
                {(() => {
                  const excl = detailRows.filter(r => r.EXCLUDED_FLAG === "Y").length;
                  const incl = detailRows.length - excl;
                  return `${incl.toLocaleString("en-IN")} applicable · ${excl.toLocaleString("en-IN")} excluded`;
                })()}
              </span>
            </div>
          )}

          {/* ── Action API feedback ── */}
          {actionLoading && (
            <div className={styles.processing}>
              <div className={styles.spinner} />
              <span>Updating records…</span>
            </div>
          )}
          {actionMsg.text && (
            <div className={`${styles.msg} ${
              actionMsg.type === "success" ? styles.msgSuccess :
              actionMsg.type === "error"   ? styles.msgError   : styles.msgInfo
            }`} style={{ marginTop:6 }}>
              <span className={styles.msgIcon}>
                {actionMsg.type === "success" ? "✓" : actionMsg.type === "error" ? "✕" : "ℹ"}
              </span>
              {actionMsg.text}
              <button
                onClick={() => setActionMsg({ type:"", text:"" })}
                style={{ marginLeft:"auto", background:"none", border:"none", cursor:"pointer", fontSize:11, opacity:0.6 }}
              >✕</button>
            </div>
          )}

          {/* Loading / Error */}
          {detailLoading && <div className={styles.processing}><div className={styles.spinner} /><span>Loading transaction records...</span></div>}
          {detailError   && <div className={`${styles.msg} ${styles.msgError}`}><span className={styles.msgIcon}>✕</span>{detailError}</div>}

          {/* Table info */}
          {detailRows.length > 0 && (
            <div className={styles.tableInfo} style={{ marginBottom:4 }}>
              Showing <strong>{detailPaged.length}</strong> of <strong>{filteredDetailRows.length}</strong> filtered
              {filteredDetailRows.length !== detailRows.length && (
                <span> (of {detailRows.length.toLocaleString("en-IN")} total)</span>
              )}
              {selectedIds.size > 0 && (
                <span style={{ marginLeft:8, color:"#1b6ca8" }}>{selectedIds.size} selected</span>
              )}
              {activeFilterCount > 0 && (
                <span style={{ marginLeft:8, color:"#d4860a" }}>
                  ▼ {activeFilterCount} column filter{activeFilterCount > 1 ? "s" : ""} active
                </span>
              )}
            </div>
          )}

          {/* ── Detail Table ── */}
          {(detailRows.length > 0 || detailLoading) && (
            <div className={styles.detailTableWrap}>
              <table className={styles.detailTable}>
                <colgroup>
                  <col style={{ width:28 }} /> {/* checkbox */}
                  {visibleCols.map(c => <col key={c.key} style={{ width: c.width }} />)}
                </colgroup>
                <thead>
                  <tr>
                    {/* Checkbox header */}
                    <th className={styles.checkboxTh}>
                      <input type="checkbox"
                        onChange={toggleAll}
                        checked={detailPaged.length > 0 && detailPaged.every(r => selectedIds.has(r.ID))}
                        style={{ cursor:"pointer" }}
                      />
                    </th>
                    {visibleCols.map(col => {
                      const fc         = filterCount(col.key);
                      const isFilterAct= isFilterActive(col.key);
                      const sortInfo   = sortOrder.find(s => s.key === col.key);
                      const sortPriority = sortOrder.findIndex(s => s.key === col.key); // 0-based
                      return (
                        <th
                          key={col.key}
                          className={`${styles.filterTh} ${isFilterAct ? styles.filterThActive : ""} ${sortInfo ? styles.filterThSorted : ""}`}
                          style={{ width: col.width }}
                        >
                          <div className={styles.filterThInner}>
                            {/* Clickable label → sort */}
                            <span
                              className={styles.filterThLabel}
                              title={`Click to sort. Shift+Click to add to multi-sort.`}
                              onClick={e => handleColSort(e, col.key)}
                              style={{ cursor:"pointer", userSelect:"none" }}
                            >
                              {col.label}
                            </span>
                            {/* Sort arrow + priority badge */}
                            {sortInfo && (
                              <span className={styles.sortIndicator} title={`Sorted ${sortInfo.dir === "asc" ? "ascending" : "descending"}${sortOrder.length > 1 ? ` (priority ${sortPriority + 1})` : ""}`}>
                                {sortInfo.dir === "asc" ? "▲" : "▼"}
                                {sortOrder.length > 1 && (
                                  <span className={styles.sortPriority}>{sortPriority + 1}</span>
                                )}
                              </span>
                            )}
                            {/* Filter active badge */}
                            {isFilterAct && (
                              <span className={styles.filterActiveBadge}>{fc}</span>
                            )}
                            {/* Filter dropdown button */}
                            <button
                              className={`${styles.filterBtn} ${isFilterAct ? styles.filterBtnActive : ""}`}
                              onClick={e => openFilter(e, col.key)}
                              title="Filter this column"
                            >▾</button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {detailPaged.length === 0 ? (
                    <tr>
                      <td colSpan={visibleCols.length + 1}>
                        <div className={styles.emptyState}>No records match the current filters.</div>
                      </td>
                    </tr>
                  ) : (
                    detailPaged.map((row, idx) => {
                      const isSel      = selectedIds.has(row.ID);
                      const isExcluded = row.EXCLUDED_FLAG === "Y" || row.EXCLUDED_FLAG === "y";
                      const rowClass   = isSel
                        ? styles.rowSelected
                        : isExcluded
                          ? styles.rowExcluded
                          : "";
                      return (
                        <tr key={row.ID || idx} className={rowClass}>
                          <td style={{ textAlign:"center", padding:"2px 4px" }}>
                            <input type="checkbox" checked={isSel}
                              onChange={() => toggleRow(row.ID)}
                              style={{ cursor:"pointer" }}
                            />
                          </td>
                          {visibleCols.map(col => {
                            const v = cellVal(col, row);
                            let cellClass = col.type === "amount" ? styles.tdRight : "";
                            let display = v;

                            // Special renders
                            if (col.key === "ENTITY_CODE") {
                              display = <span className={v === "SE" ? styles.chipSe : styles.chipSs}>{v}</span>;
                              cellClass = "";
                            } else if (col.key === "RECONCILED_FLAG") {
                              display = v === "Y"
                                ? <span className={styles.reconY}>Y</span>
                                : <span className={styles.reconN}>{v || "N"}</span>;
                              cellClass = styles.tdCenter;
                            } else if (col.key === "IS_RCM") {
                              display = v === "Y"
                                ? <span className={styles.reconY}>Y</span>
                                : <span style={{ color:"#aaa", fontSize:10 }}>{v || "N"}</span>;
                              cellClass = styles.tdCenter;
                            } else if (col.key === "EXCLUDED_FLAG") {
                              display = (v === "Y" || v === "y")
                                ? <span className={styles.exclY} title={`Excluded by ${row.EXCLUDED_BY || "?"}`}>❌</span>
                                : <span className={styles.exclN}>✅</span>;
                              cellClass = styles.tdCenter;
                            }

                            return (
                              <td key={col.key} className={cellClass} title={String(v)}>
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>

                {/* ── Summary Footer ── */}
                {filteredDetailRows.length > 0 && (
                  <tfoot>
                    <tr className={styles.summaryRow}>
                      <td colSpan={
                        1 + // checkbox
                        visibleCols.filter(c => c.type !== "amount").length
                      }>
                        <span className={styles.summaryLabel}>
                          TOTAL ({filteredDetailRows.length.toLocaleString("en-IN")} records)
                        </span>
                      </td>
                      {visibleCols.filter(c => c.type === "amount").map(col => (
                        <td key={col.key} className={styles.tdRight}>
                          {fmt(detailSummary[col.key])}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* ── Detail Pagination ── */}
          {detailRows.length > 0 && (
            <PagerBar
              page={detailPage}
              totalPages={detailTotalPages}
              total={filteredDetailRows.length}
              pageSize={detailPageSize}
              onPage={p => setDetailPage(p)}
              onPageSize={n => { setDetailPageSize(n); setDetailPage(1); }}
            />
          )}
        </Panel>

      </div>{/* /page */}

      <div style={{ padding:"0 12px" }}>

      {/* ══════════════════════════════════════════
          PANEL 7 — EXCLUSION SUMMARY
      ══════════════════════════════════════════ */}
      <Panel
        title="GST Applicability Summary — Included vs Excluded"
        open={exclSumOpen}
        onToggle={() => setExclSumOpen(o => !o)}
        badge={detailRows.length > 0 ? `${detailRows.length.toLocaleString("en-IN")} total` : null}
      >
        {detailRows.length === 0 ? (
          <div className={styles.emptyState}>Load detail records to see exclusion summary.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table} style={{ minWidth:820 }}>
              <thead>
                <tr>
                  {["Status","Records","Assessable","IGST","CGST","SGST","CESS","Total Tax","Bill Amt"].map((h,i) => (
                    <th key={i}
                      style={{ textAlign: i<=1?"left":"right", fontSize:10, fontWeight:"bold",
                               padding:"4px 8px", background:"#eaf0f6",
                               borderBottom:"2px solid #1b6ca8", whiteSpace:"nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Included row */}
                <tr style={{ background:"#f0fdf4" }}>
                  <td style={{ padding:"4px 8px", fontSize:11 }}>
                    <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
                      <span className={styles.exclN}>✅</span>
                      <strong style={{ color:"#15803d" }}>GST Applicable (Included)</strong>
                    </span>
                  </td>
                  <td style={{ textAlign:"right", padding:"4px 8px", fontSize:11, color:"#15803d", fontWeight:600 }}>
                    {exclusionSummary.included.count.toLocaleString("en-IN")}
                  </td>
                  {["ASSESSABLE_VALUE","IGST_AMOUNT","CGST_AMOUNT","SGST_AMOUNT","CESS_AMOUNT","TOTAL_TAX_VALUE","BILL_AMOUNT"].map(k => (
                    <td key={k} style={{ textAlign:"right", padding:"4px 8px", fontSize:11, color:"#15803d" }}>
                      {fmt(exclusionSummary.included[k])}
                    </td>
                  ))}
                </tr>
                {/* Excluded row */}
                <tr style={{ background:"#fef2f2" }}>
                  <td style={{ padding:"4px 8px", fontSize:11 }}>
                    <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
                      <span className={styles.exclY}>❌</span>
                      <strong style={{ color:"#b91c1c" }}>GST Not Applicable (Excluded)</strong>
                    </span>
                  </td>
                  <td style={{ textAlign:"right", padding:"4px 8px", fontSize:11, color:"#b91c1c", fontWeight:600 }}>
                    {exclusionSummary.excluded.count.toLocaleString("en-IN")}
                  </td>
                  {["ASSESSABLE_VALUE","IGST_AMOUNT","CGST_AMOUNT","SGST_AMOUNT","CESS_AMOUNT","TOTAL_TAX_VALUE","BILL_AMOUNT"].map(k => (
                    <td key={k} style={{ textAlign:"right", padding:"4px 8px", fontSize:11, color:"#b91c1c" }}>
                      {fmt(exclusionSummary.excluded[k])}
                    </td>
                  ))}
                </tr>
              </tbody>
              <tfoot>
                <tr className={styles.summaryRow}>
                  <td style={{ padding:"4px 8px" }}>GRAND TOTAL</td>
                  <td style={{ textAlign:"right", padding:"4px 8px" }}>
                    {exclusionSummary.total.count.toLocaleString("en-IN")}
                  </td>
                  {["ASSESSABLE_VALUE","IGST_AMOUNT","CGST_AMOUNT","SGST_AMOUNT","CESS_AMOUNT","TOTAL_TAX_VALUE","BILL_AMOUNT"].map(k => (
                    <td key={k} style={{ textAlign:"right", padding:"4px 8px" }}>
                      {fmt(exclusionSummary.total[k])}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>

      {/* ══════════════════════════════════════════
          PANEL 4 — MONTHLY SUMMARY
      ══════════════════════════════════════════ */}
      <Panel
        title="Monthly Summary — by Bill Date"
        open={monthSumOpen}
        onToggle={() => setMonthSumOpen(o => !o)}
        badge={monthlySummary.length > 0 ? `${monthlySummary.length} months` : null}
      >
        {filteredDetailRows.length === 0 ? (
          <div className={styles.emptyState}>Load detail records to see monthly summary.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table} style={{ minWidth:820 }}>
              <thead>
                <tr>
                  {["Month","Records","Assessable","IGST","CGST","SGST","CESS","Total Tax","Bill Amt"].map((h,i) => (
                    <th key={i} className={styles.grpFix}
                      style={{ textAlign: i<=1?"left":"right", fontSize:10, padding:"4px 8px",
                               background:"#eaf0f6", borderBottom:"2px solid #1b6ca8" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthlySummary.map((r, i) => (
                  <tr key={i} className={i%2===0?"":styles.rowAlt}>
                    <td style={{ fontWeight:600, color:"#1b4f72", padding:"3px 8px", fontSize:11 }}>{r.month}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11 }}>{r.count.toLocaleString("en-IN")}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.ASSESSABLE_VALUE)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.IGST_AMOUNT)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.CGST_AMOUNT)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.SGST_AMOUNT)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.CESS_AMOUNT)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.TOTAL_TAX_VALUE)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, fontWeight:600, color:"#1b4f72" }}>{fmt(r.BILL_AMOUNT)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={styles.summaryRow}>
                  <td style={{ padding:"3px 8px" }}>TOTAL</td>
                  <td style={{ textAlign:"right", padding:"3px 8px" }}>
                    {monthlySummary.reduce((s,r)=>s+r.count,0).toLocaleString("en-IN")}
                  </td>
                  {["ASSESSABLE_VALUE","IGST_AMOUNT","CGST_AMOUNT","SGST_AMOUNT","CESS_AMOUNT","TOTAL_TAX_VALUE","BILL_AMOUNT"].map(k => (
                    <td key={k} style={{ textAlign:"right", padding:"3px 8px" }}>
                      {fmt(monthlySummary.reduce((s,r)=>s+(r[k]||0),0))}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>

      {/* ══════════════════════════════════════════
          PANEL 5 — ADDON SUMMARY
      ══════════════════════════════════════════ */}
      <Panel
        title="Addon Summary — by Addon Code / Name"
        open={addonSumOpen}
        onToggle={() => setAddonSumOpen(o => !o)}
        badge={addonSummary.length > 0 ? `${addonSummary.length} addons` : null}
      >
        {filteredDetailRows.length === 0 ? (
          <div className={styles.emptyState}>Load detail records to see addon summary.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table} style={{ minWidth:920 }}>
              <thead>
                <tr>
                  {["Addon Code","Addon Name","Records","Assessable","IGST","CGST","SGST","CESS","Total Tax","Bill Amt"].map((h,i) => (
                    <th key={i} className={styles.grpFix}
                      style={{ textAlign: i<=1?"left":"right", fontSize:10, padding:"4px 8px",
                               background:"#eaf0f6", borderBottom:"2px solid #1b6ca8" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {addonSummary.map((r, i) => (
                  <tr key={i} className={i%2===0?"":styles.rowAlt}>
                    <td style={{ padding:"3px 8px", fontSize:11, fontWeight:600, color:"#1b4f72" }}>{r.ADDON_CODE}</td>
                    <td style={{ padding:"3px 8px", fontSize:11 }}>{r.ADDON_NAME}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11 }}>{r.count.toLocaleString("en-IN")}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.ASSESSABLE_VALUE)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.IGST_AMOUNT)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.CGST_AMOUNT)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.SGST_AMOUNT)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.CESS_AMOUNT)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, color:"#0d3a6a" }}>{fmt(r.TOTAL_TAX_VALUE)}</td>
                    <td style={{ textAlign:"right", padding:"3px 8px", fontSize:11, fontWeight:600, color:"#1b4f72" }}>{fmt(r.BILL_AMOUNT)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={styles.summaryRow}>
                  <td colSpan={2} style={{ padding:"3px 8px" }}>TOTAL</td>
                  <td style={{ textAlign:"right", padding:"3px 8px" }}>
                    {addonSummary.reduce((s,r)=>s+r.count,0).toLocaleString("en-IN")}
                  </td>
                  {["ASSESSABLE_VALUE","IGST_AMOUNT","CGST_AMOUNT","SGST_AMOUNT","CESS_AMOUNT","TOTAL_TAX_VALUE","BILL_AMOUNT"].map(k => (
                    <td key={k} style={{ textAlign:"right", padding:"3px 8px" }}>
                      {fmt(addonSummary.reduce((s,r)=>s+(r[k]||0),0))}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>

      {/* ══════════════════════════════════════════
          PANEL 6 — VENDOR PERIOD-WISE PIVOT
      ══════════════════════════════════════════ */}
      <Panel
        title="Vendor Summary — Bill Amount by Month (Period-wise)"
        open={vendorSumOpen}
        onToggle={() => setVendorSumOpen(o => !o)}
        badge={vendorPivot.vendors.length > 0 ? `${vendorPivot.vendors.length} vendors` : null}
      >
        {filteredDetailRows.length === 0 ? (
          <div className={styles.emptyState}>Load detail records to see vendor pivot.</div>
        ) : (
          <>
          <div className={styles.tableInfo} style={{ marginBottom:4 }}>
            Showing <strong>{vendorPaged.length}</strong> of <strong>{vendorPivot.vendors.length}</strong> vendors
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}
              style={{ minWidth: 200 + vendorPivot.months.length * 100 }}>
              <thead>
                <tr>
                  <th style={{ background:"#eaf0f6", fontSize:10, fontWeight:"bold",
                               padding:"4px 8px", textAlign:"left", borderBottom:"2px solid #1b6ca8",
                               minWidth:72, position:"sticky", left:0, zIndex:2 }}>
                    Party Code
                  </th>
                  <th style={{ background:"#eaf0f6", fontSize:10, fontWeight:"bold",
                               padding:"4px 8px", textAlign:"left", borderBottom:"2px solid #1b6ca8",
                               minWidth:160, position:"sticky", left:72, zIndex:2 }}>
                    Party Name
                  </th>
                  {vendorPivot.months.map(m => (
                    <th key={m.key}
                      style={{ background:"#d0e4f7", fontSize:10, fontWeight:"bold", color:"#0d3a6a",
                               padding:"4px 8px", textAlign:"right", borderBottom:"2px solid #1b6ca8",
                               minWidth:90, whiteSpace:"nowrap" }}>
                      {m.label}
                    </th>
                  ))}
                  <th style={{ background:"#d4edda", fontSize:10, fontWeight:"bold", color:"#1a5c2a",
                               padding:"4px 8px", textAlign:"right", borderBottom:"2px solid #82c99a",
                               minWidth:100, whiteSpace:"nowrap" }}>
                    Grand Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {vendorPaged.map((v, i) => (
                  <tr key={i}>
                    <td style={{ padding:"2px 8px", fontSize:11, fontWeight:600,
                                 color:"#1b4f72", position:"sticky", left:0,
                                 background: i%2===0?"#fff":"#f7fafc", zIndex:1 }}>
                      {v.PARTY_CODE}
                    </td>
                    <td style={{ padding:"2px 8px", fontSize:11,
                                 position:"sticky", left:72,
                                 background: i%2===0?"#fff":"#f7fafc", zIndex:1 }}>
                      {v.PARTY_NAME}
                    </td>
                    {vendorPivot.months.map(m => (
                      <td key={m.key}
                        style={{ padding:"2px 8px", fontSize:11, textAlign:"right",
                                 color: v.months[m.key] ? "#0d3a6a" : "#ccc" }}>
                        {v.months[m.key] ? fmt(v.months[m.key]) : "—"}
                      </td>
                    ))}
                    <td style={{ padding:"2px 8px", fontSize:11, textAlign:"right",
                                 fontWeight:700, color:"#1a5c2a", background: i%2===0?"#f0fdf4":"#e8f8ee" }}>
                      {fmt(v.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={styles.summaryRow}>
                  <td colSpan={2} style={{ padding:"3px 8px" }}>TOTAL</td>
                  {vendorPivot.months.map(m => (
                    <td key={m.key} style={{ textAlign:"right", padding:"3px 8px" }}>
                      {fmt(vendorPivot.vendors.reduce((s,v) => s+(v.months[m.key]||0), 0))}
                    </td>
                  ))}
                  <td style={{ textAlign:"right", padding:"3px 8px" }}>
                    {fmt(vendorPivot.vendors.reduce((s,v) => s+v.total, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <PagerBar
            page={vendorPage}
            totalPages={vendorTotalPages}
            total={vendorPivot.vendors.length}
            pageSize={vendorPageSize}
            onPage={p => setVendorPage(p)}
            onPageSize={n => { setVendorPageSize(n); setVendorPage(1); }}
          />
          </>
        )}
      </Panel>
      </div>{/* /summary panels wrapper */}

      {/* ── Floating Filter Dropdown (portal-style, fixed position) ── */}
      <FilterDropdown />

      {/* ── Column Visibility Panel (portal-style, fixed position) ── */}
      <ColVisPanel />

    </div>
  );
};

export default TransferErpData;
